import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Row, Col } from "antd";
import type { CustomerDTO } from "@ifa/utils";
import styles from "../FnaSection.module.css";
import localStyles from "./FnaBasicInfoPlaceholder.module.css";
import {
  basicFnaConfig,
  zuuReadonlyFinancialConfig,
  type FinancialSnapshot,
  type Question,
  type ZuuReadonlyFinancialBlock,
  type ZuuReadonlyFinancialFieldKey,
} from "./fnaBasicConfig";
import { computeSummaries, evaluateConditions, formatMoney } from "./fnaBasicUtils";
import { FnaField } from "./FnaField";
import { AmountInput } from "../../../../../../components/form/AmountInput";
import { shouldShowZuuRetirementFields } from "../../../../../order-fna/zuuValidation";
import {
  resolveZuuFinancialInfoDisplay,
  type ZuuFinancialInfoDisplay,
} from "./resolveZuuFinancialInfoDisplay";

type Props = {
  initialData?: FinancialSnapshot;
  financial?: CustomerDTO["financial"] | null;
  policyholder?: CustomerDTO["policyholder"] | null;
  isZuuFinancialStepMode?: boolean;
  onSave?: (data: FinancialSnapshot) => void;
  onEditingChange?: (editing: boolean) => void;
  mode: "view" | "edit";
};

const emptySnapshot: FinancialSnapshot = {
  income: {},
  expense: {},
  currentAssets: {},
  nonCurrentAssets: {},
  liabilities: {},
  protection: {},
};

const sectionTitleStyle: React.CSSProperties = {
  color: "#23262C",
  fontWeight: "600",
  marginBottom: "16px",
  fontSize: "18px",
  lineHeight: "24px",
};

const headerBoxStyle: React.CSSProperties = {
  background: "var(--theme-primary)",
  borderRadius: "6px",
  padding: "18px 24px",
  fontWeight: "500",
  fontSize: "18px",
  color: "#FFFFFF",
  lineHeight: "24px",
  display: "flex",
  justifyContent: "space-between",
};

const sectionBoxStyle: React.CSSProperties = {
  marginBottom: 16,
};

const subSectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 16,
  color: "#333",
  fontSize: 14,
  fontWeight: 600,
};

const sectionDividerStyle: React.CSSProperties = {
  background: "#EBEBEB",
  width: "100%",
  height: "1px",
  margin: "16px 0",
};

const blockDividerStyle: React.CSSProperties = {
  background: "#EBEBEB",
  width: "100%",
  height: "1px",
  margin: "32px 0",
};

export type FnaBasicInfoHandle = {
  save: () => FinancialSnapshot;
  cancel: () => void;
  isDirty: () => boolean;
};

export const FnaBasicInfoPlaceholder = forwardRef<FnaBasicInfoHandle, Props>(
  function FnaBasicInfoPlaceholder(
    {
      initialData,
      financial,
      policyholder,
      isZuuFinancialStepMode = false,
      onSave,
      onEditingChange,
      mode,
    }: Props,
    ref,
  ) {
    const [snapshot, setSnapshot] = useState<FinancialSnapshot>(
      initialData ?? emptySnapshot,
    );
    const [draft, setDraft] = useState<FinancialSnapshot>(snapshot);
    const [isDirty, setIsDirty] = useState(false);
    const isEditing = mode === "edit";

    useEffect(() => {
      setSnapshot(initialData ?? emptySnapshot);
      setDraft(initialData ?? emptySnapshot);
      setIsDirty(false);
    }, [initialData]);

    useEffect(() => {
      onEditingChange?.(isEditing && isDirty);
      return () => onEditingChange?.(false);
    }, [isEditing, isDirty, onEditingChange]);

    const computeDirty = (nextDraft: FinancialSnapshot) =>
      JSON.stringify(nextDraft) !== JSON.stringify(snapshot);

    const summaries = useMemo(() => computeSummaries(draft), [draft]);
    const zuuFinancialInfoDisplay = useMemo(
      () => resolveZuuFinancialInfoDisplay(financial),
      [financial],
    );
    const showRetirementFields = useMemo(
      () => shouldShowZuuRetirementFields(policyholder ?? undefined),
      [policyholder],
    );

    const handleValueChange = (
      question: Question,
      value: number | string | boolean | null,
    ) => {
      setDraft((prev) => {
        const next: FinancialSnapshot = {
          ...prev,
          [question.section]: {
            ...(prev[question.section] as Record<string, any>),
            [question.key]: value === null ? undefined : value,
          },
        };
        setIsDirty(computeDirty(next));
        return next;
      });
    };

    const handleSave = useCallback(() => {
      setSnapshot(draft);
      onSave?.(draft);
      setIsDirty(false);
      return draft;
    }, [draft, onSave]);

    const handleCancel = useCallback(() => {
      setDraft(snapshot);
      setIsDirty(false);
    }, [snapshot]);

    useImperativeHandle(
      ref,
      () => ({
        save: handleSave,
        cancel: handleCancel,
        isDirty: () => isDirty,
      }),
      [handleSave, handleCancel, isDirty],
    );

    const renderControl = (question: Question) => {
      const value = (draft[question.section] as any)?.[question.key];
      const visible = evaluateConditions(question.visibleWhen, draft);
      if (!visible) return null;

      const isInlineSwitch =
        question.kind === "switch" && question.variant === "inlineSwitch";

      if (isInlineSwitch) {
        return (
          <Col
            span={question.colSpan ?? 24}
            key={`${question.section}-${question.key}`}
          >
            <FnaField
              question={question}
              value={value}
              mode={mode}
              onChange={(val) => handleValueChange(question, val as any)}
            />
          </Col>
        );
      }

      return (
        <Col
          span={question.colSpan ?? 12}
          className={localStyles.fieldRow}
          key={`${question.section}-${question.key}`}
        >
          <div className={localStyles.fieldLabel}>{question.label}</div>
          <FnaField
            question={question}
            value={value}
            mode={mode}
            onChange={(val) => handleValueChange(question, val as any)}
          />
        </Col>
      );
    };

    const getZuuQuestionSummary = (fieldKey: ZuuReadonlyFinancialFieldKey) =>
      zuuFinancialInfoDisplay[fieldKey] ?? 0;

    const getZuuHeaderSummary = (
      summaryKey: ZuuReadonlyFinancialBlock["summaryKey"],
      display: ZuuFinancialInfoDisplay,
    ) => {
      if (summaryKey === "surplusDeficit") {
        return (
          (display.monthlyIncomeLast24Months ?? 0) -
          (display.monthlyExpenseLast24Months ?? 0)
        );
      }

      return (
        (display.currentLiquidAssets ?? 0) +
        (showRetirementFields
          ? (display.additionalLiquidAssetsAfterRetirement ?? 0)
          : 0)
      );
    };

    const renderZuuBlock = (block: ZuuReadonlyFinancialBlock) => {
      const visibleQuestions = block.questions.filter(
        (question) => !question.retirementOnly || showRetirementFields,
      );

      return (
        <div key={block.key}>
          <div style={{ ...headerBoxStyle, marginBottom: 16 }}>
            <span>{block.headerLabel}</span>
            <strong>
              小計:{" "}
              {formatMoney(getZuuHeaderSummary(block.summaryKey, zuuFinancialInfoDisplay))}{" "}
              HKD
            </strong>
          </div>

          <div style={sectionBoxStyle}>
            <div className={localStyles.fieldLabel}>{block.headerNote}</div>
          </div>

          {visibleQuestions.map((question, index) => (
            <React.Fragment key={question.code}>
              <div style={sectionBoxStyle}>
                <div style={subSectionHeaderStyle}>
                  <span style={{ color: "#23262C", fontSize: "16px" }}>
                    {question.code}) {question.title}
                  </span>
                  <span style={{ color: "#333", fontWeight: 500 }}>
                    小計: {formatMoney(getZuuQuestionSummary(question.fieldKey))} HKD
                  </span>
                </div>
                <div className={localStyles.fieldRow}>
                  <div className={localStyles.fieldLabel}>{question.note}</div>
                  {question.warning ? (
                    <div className={localStyles.fieldLabel}>{question.warning}</div>
                  ) : null}
                  <AmountInput
                    style={{ width: "100%" }}
                    value={zuuFinancialInfoDisplay[question.fieldKey]}
                    readOnly
                    placeholder="--"
                    suffix={question.suffix}
                    suffixVariant="box"
                  />
                </div>
              </div>
              {index !== visibleQuestions.length - 1 ? (
                <div style={blockDividerStyle} />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      );
    };

    if (isZuuFinancialStepMode && mode === "view") {
      return (
        <div className={styles.fnaCard} style={{ padding: 16 }}>
          <div style={sectionTitleStyle}>一、財務資料</div>
          {zuuReadonlyFinancialConfig.map((block, blockIndex) => (
            <React.Fragment key={block.key}>
              {renderZuuBlock(block)}
              {blockIndex !== zuuReadonlyFinancialConfig.length - 1 ? (
                <div style={sectionDividerStyle} />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      );
    }

    return (
      <div className={styles.fnaCard} style={{ padding: 16 }}>
        {basicFnaConfig.map((section, sectionIdx) => (
          <div key={section.title}>
            <div style={sectionTitleStyle}>{section.title}</div>

            {section.blocks.map((block, blockIdx) => (
              <div key={block.key}>
                {block.headerSummaryKey && (
                  <div style={{ ...headerBoxStyle, marginBottom: 16 }}>
                    <span>{block.headerLabel}</span>
                    <strong>
                      {block.headerPrefix ?? ""}
                      {summaries[block.headerSummaryKey].toLocaleString()}
                      {block.headerValueSuffix ?? ""}
                    </strong>
                  </div>
                )}

                {block.groups.map((group, groupIdx) => (
                  <React.Fragment key={group.key}>
                    <div style={sectionBoxStyle}>
                      <div style={subSectionHeaderStyle}>
                        <span style={{ color: "#23262C", fontSize: "16px" }}>
                          {group.title}
                        </span>
                        {group.summaryKey && (
                          <span style={{ color: "#333", fontWeight: 500 }}>
                            {group.summaryLabel ??
                              block.summaryLabelPrefix ??
                              ""}
                            {summaries[group.summaryKey].toLocaleString()}
                            {group.summaryValueSuffix ?? ""}
                          </span>
                        )}
                      </div>
                      <Row gutter={group.gutter ?? [16, 24]}>
                        {group.questions.map((q) => renderControl(q))}
                      </Row>
                    </div>
                    {groupIdx !== block.groups.length - 1 ? (
                      <div style={blockDividerStyle} />
                    ) : null}
                  </React.Fragment>
                ))}

                {blockIdx !== section.blocks.length - 1 ? (
                  <div style={blockDividerStyle} />
                ) : null}
              </div>
            ))}

            {sectionIdx !== basicFnaConfig.length - 1 ? (
              <div style={sectionDividerStyle} />
            ) : null}
          </div>
        ))}
      </div>
    );
  },
);
